/**
 * VIB-726: Cancel-dialog for godkjente/avslåtte timebank-transaksjoner.
 *
 * Begrunnelse er obligatorisk og sendes til berørt bruker som varsel.
 * Pending-rader bruker vanlig avslå-flyt — dette dialog vises ikke for dem.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  useCancelTimebankTransaction,
  type TimebankTransaction,
} from "@/hooks/use-timebank";
import { formatHours } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";

interface Props {
  transaction: TimebankTransaction | null;
  onClose: () => void;
}

export function CancelTransactionDialog({ transaction, onClose }: Props) {
  const [reason, setReason] = useState("");
  // VIB-2766: `deleted_by` skal bære den faktisk innloggede, se samme
  // begrunnelse i TimebankApprovalsPage.
  const { user: selfUser } = useAuth();
  const cancelMut = useCancelTimebankTransaction();

  const handleConfirm = () => {
    if (!transaction || !selfUser) return;
    cancelMut.mutate(
      {
        transaction,
        reason,
        decided_by: selfUser!.id,
      },
      {
        onSuccess: () => {
          setReason("");
          onClose();
        },
      },
    );
  };

  const handleClose = () => {
    setReason("");
    onClose();
  };

  return (
    <Dialog
      open={!!transaction}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">
            Fjern timebank-transaksjon
          </DialogTitle>
        </DialogHeader>
        {transaction && (
          <p className="text-sm text-muted-foreground">
            {formatHours(transaction.hours)}{" "}
            {transaction.kind === "deposit" ? "oppsparing" : "uttak"} for{" "}
            <span className="font-medium text-foreground">
              {transaction.user?.full_name ?? "ukjent bruker"}
            </span>
            . Begrunnelse sendes som varsel til brukeren og blir lagret som
            audit-spor.
          </p>
        )}
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Begrunnelse (påkrevd)
          </label>
          <textarea
            rows={3}
            className="mt-1 w-full rounded-md border-[1.5px] border-foreground bg-background px-2 py-1.5 text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Hvorfor fjernes denne transaksjonen?"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={cancelMut.isPending}
          >
            Avbryt
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!reason.trim() || cancelMut.isPending}
          >
            {cancelMut.isPending ? (
              <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <SrIcon name="trash-2" className="w-4 h-4 mr-1.5" />
            )}
            Fjern transaksjon
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
